import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import BedRockContext from "../../BedRockContext";
import { PageTitle } from "../../../_metronic/layout/core";
import { GenericTable } from "../generic/table/GenericTable";
import { defaultList } from "../generic/InitialStates";
import { HeaderColumnFormatter } from "../../column-formatters/HeaderColumnFormatter";
import { ActionsColumnFormatter } from "../../column-formatters/ActionsColumnFormatter";
import { withSyncDot } from "../../column-formatters/SyncStatusColumnFormatter";
import { ENTITY_ID_CUSTOMER_SALE } from "../generic/Constants";
import { DMSDateColumnFormatter } from "../../column-formatters/DMSDateColumnFormatter";
import { SyncProgressDialog } from "../../generic-components/SyncProgressDialog";
import { useSyncOperations } from "../../hooks/useSyncOperations";
import { useSyncEnabled } from "../../hooks/useSyncEnabled";
import { EwayBillDetailDialog } from "./EwayBillDetailDialog";
import { EInvoiceDetailDialog } from "./EInvoiceDetailDialog";
import { getOrgInfo } from "../generic/crud";
import { GstDocumentsCell } from "./GstDocumentsCell";

// Format currency
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

export function SalesListPage() {
    const myData = useContext(BedRockContext);
    const navigate = useNavigate();
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);
    // Sale whose e-Way bill detail/recovery popup is open
    const [ewayDialogSale, setEwayDialogSale] = useState(null);
    // GST Services add-on: the E-Way Bill column only exists for enabled orgs.
    // Seed from the last-known value (cached) so the column renders instantly instead
    // of waiting on the org refresh round-trip; the fetch below corrects it if it changed.
    const [gstEnabled, setGstEnabled] = useState(() => {
        const cached = localStorage.getItem("gstServicesEnabled");
        return cached === null ? null : cached === "true";
    });
    // Sale whose e-Invoice detail/recovery popup is open
    const [einvoiceDialogSale, setEinvoiceDialogSale] = useState(null);
    // e-Invoicing opt-in, cached the same way so the column renders without a round-trip
    const [einvoiceEnabled, setEinvoiceEnabled] = useState(
        () => localStorage.getItem("einvoiceEnabled") === "true"
    );

    useEffect(() => {
        getOrgInfo()
            .then(({ data }) => {
                const enabled = !!data?.gstServicesEnabled;
                setGstEnabled(enabled);
                localStorage.setItem("gstServicesEnabled", String(enabled));
                const einvoice = enabled && !!data?.einvoiceEnabled;
                setEinvoiceEnabled(einvoice);
                localStorage.setItem("einvoiceEnabled", String(einvoice));
            })
            .catch(() => setGstEnabled((prev) => (prev === null ? false : prev)));
    }, []);

    // Check if entity is sync-enabled and get push/pull capabilities
    const { syncEnabled, pushEnabled, pullEnabled } = useSyncEnabled(ENTITY_ID_CUSTOMER_SALE);

    const reloadData = useCallback(() => {
        setReload(prev => prev + 1);
    }, []);

    // Use sync operations hook with push/pull capabilities
    const {
        showDialog: showSyncDialog,
        syncType,
        isInProgress,
        syncResult,
        pushToServer,
        pullFromServer,
        closeDialog: closeSyncDialog
    } = useSyncOperations(ENTITY_ID_CUSTOMER_SALE, reloadData, { pushEnabled, pullEnabled });

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // View sale details (read-only)
    function openViewDialog(row) {
        navigate(`/sale-new/view/${row.id}`);
    }

    // Print receipt
    function openPrintDialog(row) {
        navigate(`/sale-print/${row.id}`);
    }

    // Build columns dynamically based on sync enabled status
    const columns = useMemo(() => {
        const ticketFormatter = (cell, row) => (
            <a href="#" onClick={(e) => { e.preventDefault(); openViewDialog(row); }}>
                {HeaderColumnFormatter(cell || 'N/A', row)}
            </a>
        );
        const baseColumns = [
            {
                dataField: "ticketSequenceNumber",
                text: "Ticket #",
                search: true,
                formatter: syncEnabled ? withSyncDot(ticketFormatter) : ticketFormatter,
                headerStyle: { width: '120px' }
            },
            {
                dataField: "salesCustomer.name",
                text: "Customer",
                search: true,
                formatter: (cell, row) => row.salesCustomer?.name || '-'
            },
            {
                dataField: "vehicleNumber",
                text: "Vehicle",
                formatter: (cell) => cell || '-',
                headerStyle: { width: '130px' }
            },
            {
                // salesSecondStepDate IS the sale date (both sale types); the GenericTable
                // sortBy below must stay on the same field the column shows.
                dataField: "salesSecondStepDate",
                text: "Sale Date",
                formatter: DMSDateColumnFormatter,
                headerStyle: { width: '120px' }
            },
            {
                dataField: "itemsList",
                text: "Items",
                formatter: (cell) => {
                    if (!cell || cell.length === 0) return '-';
                    // Inventory items first, then services, so the two groups read cleanly
                    const ordered = [...cell].sort((a, b) => {
                        const rank = t => (t === 'SERVICE' ? 1 : 0);
                        return rank(a.itemType) - rank(b.itemType);
                    });
                    return (
                        <div className="d-flex flex-column gap-1" style={{ maxWidth: '210px' }}>
                            {ordered.map((i, idx) => {
                                const isService = i.itemType === 'SERVICE';
                                const name = i.inventoryItemName || (isService ? 'Service' : 'Item');
                                return (
                                    <span
                                        key={idx}
                                        className={`badge ${isService ? 'badge-light-success' : 'badge-light-primary'} d-inline-flex align-items-center fw-semibold`}
                                        style={{ maxWidth: '100%' }}
                                        title={name}
                                    >
                                        <span
                                            style={{
                                                width: '5px',
                                                height: '5px',
                                                borderRadius: '50%',
                                                flexShrink: 0,
                                                marginRight: '5px',
                                                backgroundColor: 'currentColor'
                                            }}
                                        />
                                        <span className="text-truncate">{name}</span>
                                    </span>
                                );
                            })}
                        </div>
                    );
                },
                headerStyle: { width: '210px' }
            },
            {
                dataField: "qty",
                text: "Qty",
                isDummyField: true,
                formatter: (_cell, row) => {
                    const cell = row.itemsList;
                    if (!cell || cell.length === 0) return '-';
                    const totals = {};
                    cell.forEach(i => {
                        if (i.itemType !== 'INVENTORY') return;
                        const qty = parseFloat(i.quantity) || 0;
                        if (!qty) return;
                        const unit = (i.unitSymbol || '').trim();
                        totals[unit] = (totals[unit] || 0) + qty;
                    });
                    const entries = Object.entries(totals);
                    if (entries.length === 0) return '-';
                    return (
                        <div className="d-flex flex-column gap-1" style={{ whiteSpace: 'nowrap' }}>
                            {entries.map(([unit, qty]) => (
                                <div key={unit} className="d-flex align-items-baseline gap-1">
                                    <span className="fw-bold text-gray-800" style={{ fontSize: '0.95rem' }}>
                                        {Number.isInteger(qty) ? qty : qty.toFixed(2)}
                                    </span>
                                    {unit && (
                                        <span className="text-gray-500 fw-normal" style={{ fontSize: '0.72rem' }}>
                                            {unit}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                },
                headerStyle: { width: '90px' }
            },
            {
                dataField: "paymentsList",
                text: "Payments",
                formatter: (cell) => {
                    if (!cell || cell.length === 0) return <span className="badge bg-danger">Unpaid</span>;
                    const total = cell.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
                    return (
                        <span className="text-success fw-semibold">
                            {formatCurrency(total)}
                        </span>
                    );
                },
                headerStyle: { width: '130px' }
            },
            // One column for both government documents. They were previously split across
            // "E-Way Bill" and "E-Invoice", which made the operator look in two places to
            // answer a single question: what has been filed for this sale?
            ...(gstEnabled === true ? [{
                dataField: "gstDocuments",
                text: "GST Documents",
                formatter: (_cell, row) => (
                    <GstDocumentsCell
                        row={row}
                        einvoiceEnabled={einvoiceEnabled}
                        onOpenEway={() => setEwayDialogSale(row)}
                        onOpenEinvoice={() => setEinvoiceDialogSale(row)}
                    />
                ),
                headerStyle: { width: '190px' }
            }] : []),
        ];

        // Add actions column
        baseColumns.push({
            dataField: "action",
            text: "Actions",
            headerStyle: { width: "180px" },
            formatter: ActionsColumnFormatter,
            formatExtraData: {
                buttons: [
                    {
                        name: "View",
                        color: "info",
                        icon: "eye",
                        method: openViewDialog,
                    },
                    {
                        name: "Print",
                        color: "success",
                        icon: "print",
                        method: openPrintDialog,
                    },
                ],
            },
            classes: "text-right pr-0",
            headerClasses: "text-right pr-3",
        });

        return baseColumns;
    }, [syncEnabled, gstEnabled, einvoiceEnabled]);

    // Add new sale
    function addButtonClicked() {
        navigate('/sale-new/new');
    }

    useEffect(() => {
        if (myData && myData.setBaseData) {
            // Build toolbar buttons based on sync capabilities
            const toolBarButtons = [];

            if (syncEnabled && pushEnabled) {
                toolBarButtons.push({
                    name: "Push to Server",
                    color: "info",
                    icon: "fa fa-cloud-upload-alt",
                    onClick: pushToServer,
                });
            }

            if (syncEnabled && pullEnabled) {
                toolBarButtons.push({
                    name: "Pull from Server",
                    color: "success",
                    icon: "fa fa-cloud-download-alt",
                    onClick: pullFromServer,
                });
            }

            toolBarButtons.push({
                name: "Record 1 Step Sale",
                color: "primary",
                icon: "fa fa-plus",
                onClick: addButtonClicked,
            });

            myData.setBaseData({ toolBarButtons });
        }

        setPageLoadComplete(true);
    }, [pushToServer, pullFromServer, syncEnabled, pushEnabled, pullEnabled]);

    const BaseCard = () => (
        <div className="card card-body">
            {/* Sync Info Banner */}
            {syncEnabled && (
                <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
                    <i className="fas fa-info-circle me-3 fs-3"></i>
                    <div>
                        <strong>Data Retention Policy</strong>
                        <br />
                        <small>
                            Synced records (marked with <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',backgroundColor:'#50cd89',verticalAlign:'middle'}}></span>) will be automatically
                            removed from this device after <strong>2 days</strong>. These records are safely backed up on the server
                            and can be accessed from the main system.
                        </small>
                    </div>
                </div>
            )}

            {pageLoadComplete && (
                <GenericTable
                    reload={reload}
                    defaultData={defaultList}
                    columns={columns}
                    entityId={ENTITY_ID_CUSTOMER_SALE}
                    fixedFilterColumn="isPartialRecord"
                    fixedFilterData={false}
                    sortBy="salesSecondStepDate"
                    sortOrder="desc"
                />
            )}
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Sales List</PageTitle>
            <BaseCard />
            {syncEnabled && (
                <SyncProgressDialog
                    show={showSyncDialog}
                    onClose={closeSyncDialog}
                    syncType={syncType}
                    isInProgress={isInProgress}
                    syncResult={syncResult}
                />
            )}
            <EwayBillDetailDialog
                show={!!ewayDialogSale}
                sale={ewayDialogSale}
                onHide={() => setEwayDialogSale(null)}
                onUpdated={() => setReload((r) => r + 1)}
            />

            <EInvoiceDetailDialog
                show={!!einvoiceDialogSale}
                sale={einvoiceDialogSale}
                onHide={() => setEinvoiceDialogSale(null)}
                onUpdated={() => setReload((r) => r + 1)}
            />
        </>
    );
}
